/**
 * SvelteKit API Client wrapper for Dr. Lotto API
 *
 * This module provides a configured API client instance and helper functions
 * for interacting with the Dr. Lotto API in SvelteKit applications.
 */

import { createClient, type Client } from '$lib/api/client';
import { dev } from '$app/environment';

// API base URLs for different environments
const API_BASE_URL = 'https://api.dr-lotto.com';

/**
 * Pre-configured API client instance
 * This client is ready to use throughout your SvelteKit application
 */
export const apiClient: Client = createClient({
	baseUrl: API_BASE_URL
});

/**
 * Create a custom API client with specific configuration
 * Useful for testing or when you need different settings
 */
export function createApiClient(options: {
	baseUrl?: string;
	headers?: Record<string, string>;
	fetch?: typeof fetch;
}) {
	return createClient({
		baseUrl: options.baseUrl ?? API_BASE_URL,
		headers: options.headers,
		fetch: options.fetch
	});
}

/**
 * Create an API client that uses SvelteKit's fetch function
 * This is useful in load functions where you want to use the enhanced fetch
 */
export function createServerApiClient(fetch: typeof globalThis.fetch) {
	return createClient({
		baseUrl: API_BASE_URL,
		fetch
	});
}

/**
 * Convert a slug format (e.g., "fl") to state abbreviation format (e.g., "FL")
 * Used for API calls that require uppercase state abbreviations
 */
export function slugToStateAbbr(slug: string): string {
	return slug.toUpperCase();
}

// Re-export commonly used types for convenience
export type {
	GameSchema as Game,
	StateSchema as State,
	ReportSchema as Report,
	GameImageUrlSchema as GameImageUrl,
	PrizeTierSchema as PrizeTier,
	GetGamesByStateGamesStateStateAbbrGetData,
	GetGamesByStateGamesStateStateAbbrGetResponse
} from '$lib/api';
